/**
 * Y8 AFP Ads SDK — init, ads, cloud save/load, leaderboard submit, blacklist gate.
 * Non-Y8 builds set VITE_WITH_Y8=false so this module never touches window.y8.
 */

import {
  Y8_APP_ID,
  Y8_GAME_ID,
  Y8_LEADERBOARD_TABLE,
  Y8_CLOUD_KEY_PROGRESS,
  Y8_CLOUD_KEY_BEST_SCORE,
  Y8_CLOUD_KEY_ECONOMY,
  Y8_OFFICIAL_GAME_URL,
} from "../config/y8-bridge-config.js";
import {
  INTERSTITIAL_AD_NAME_NEXT_DAY,
  INTERSTITIAL_AD_NAME_PLAY_AGAIN,
} from "../config/ad-placements.js";
import { normalizeLeaderboardScore, sanitizeLeaderboardName } from "../leaderboard/leaderboard-client.js";
import { devLog } from "./dev-log.js";

var initPromise = null;
var enabled = false;
var sdkInstance = null;
/** @type {null | { nickname?: string }} */
var authUser = null;
var adAudioSyncInstalled = false;
/** @type {{ setMuted?: (muted: boolean) => void, setBgmPaused?: (paused: boolean) => void } | null} */
var adAudioSyncSoundManager = null;

function isY8Build() {
  return typeof import.meta !== "undefined" && import.meta.env && import.meta.env.VITE_WITH_Y8 === "true";
}

function getY8Global() {
  if (!isY8Build() || typeof window === "undefined") return null;
  return window.y8 || null;
}

/** Matches Y8 PDF/GitHub: listen for ready first; timeout if the async script never arrives. */
var Y8_SDK_READY_TIMEOUT_MS = 5000;

function resolveSdkFactory() {
  var y8 = getY8Global();
  return y8 && typeof y8.sdk === "function" ? y8.sdk() : null;
}

/**
 * Wait for `y8.sdk()` per Y8 docs: always register `y8sdk.ready`, then call
 * `emitReadyEvent` if the global already exists (script loaded before the listener).
 * Never bail just because `window.y8` is still null — the script is async.
 */
function waitForSdkFactory() {
  return new Promise(function (resolve) {
    var immediate = resolveSdkFactory();
    if (immediate) {
      resolve(immediate);
      return;
    }

    var settled = false;
    var timeoutId = 0;

    function finish(result) {
      if (settled) return;
      settled = true;
      if (typeof window !== "undefined") {
        window.removeEventListener("y8sdk.ready", onReady);
      }
      if (timeoutId) clearTimeout(timeoutId);
      resolve(result);
    }

    function onReady() {
      finish(resolveSdkFactory());
    }

    window.addEventListener("y8sdk.ready", onReady, { once: true });

    var y8 = getY8Global();
    if (y8 && typeof y8.emitReadyEvent === "function") {
      y8.emitReadyEvent();
    }

    // emitReadyEvent may make sdk() usable synchronously without re-firing the event
    var afterEmit = resolveSdkFactory();
    if (afterEmit) {
      finish(afterEmit);
      return;
    }

    timeoutId = setTimeout(function () {
      finish(null);
    }, Y8_SDK_READY_TIMEOUT_MS);
  });
}

function refreshEnabled() {
  enabled = !!(isY8Build() && sdkInstance);
  return enabled;
}

function getSdk() {
  return sdkInstance;
}

function hasLoggedInUser() {
  return !!(authUser && typeof authUser === "object");
}

function muteGameAudio(soundManager) {
  if (!soundManager || typeof soundManager.setMuted !== "function") return;
  soundManager.setMuted(true);
  if (typeof soundManager.setBgmPaused === "function") {
    soundManager.setBgmPaused(true);
  }
}

function restoreGameAudio(soundManager) {
  if (!soundManager || typeof soundManager.setMuted !== "function") return;
  soundManager.setMuted(false);
  if (typeof soundManager.setBgmPaused === "function") {
    soundManager.setBgmPaused(false);
  }
}

function mapLocalKeyToCloudKey(localKey, progressStorageKey, bestScoreStorageKey, economyStorageKey) {
  if (localKey === progressStorageKey) return Y8_CLOUD_KEY_PROGRESS;
  if (localKey === bestScoreStorageKey) return Y8_CLOUD_KEY_BEST_SCORE;
  if (localKey === economyStorageKey) return Y8_CLOUD_KEY_ECONOMY;
  return null;
}

export const Y8Bridge = {
  ensureInitialized: function () {
    if (initPromise) return initPromise;
    if (!isY8Build()) {
      enabled = false;
      initPromise = Promise.resolve();
      return initPromise;
    }
    initPromise = waitForSdkFactory()
      .then(function (factory) {
        if (!factory) {
          enabled = false;
          return;
        }
        sdkInstance = factory;
        var appConfig = {
          appId: Y8_APP_ID,
          autoLogin: true,
        };
        var adConfig = {
          gameId: Y8_GAME_ID,
          test: typeof import.meta !== "undefined" && import.meta.env && import.meta.env.DEV === true,
          preloadAdBreaks: "auto",
          sound: "on",
          onReady: function () {
            devLog("[Y8Bridge] ads ready");
          },
        };
        if (typeof sdkInstance.init === "function") {
          sdkInstance.init(appConfig, adConfig);
        }
        if (typeof sdkInstance.onAuth === "function") {
          sdkInstance.onAuth(function (user, error) {
            if (error) {
              console.warn("[Y8Bridge] auth error:", error);
              authUser = null;
              return;
            }
            authUser = user || null;
          });
        }
        refreshEnabled();
      })
      .catch(function (err) {
        enabled = false;
        console.warn("[Y8Bridge] init failed:", err);
      });
    return initPromise;
  },

  isEnabled: function () {
    return !!enabled;
  },

  isLoggedIn: function () {
    return hasLoggedInUser();
  },

  getNickname: function () {
    if (!authUser || typeof authUser.nickname !== "string") return null;
    var name = authUser.nickname.trim();
    return name || null;
  },

  checkBlacklisted: function () {
    if (!enabled) return Promise.resolve(false);
    var sdk = getSdk();
    if (!sdk || typeof sdk.isBlacklisted !== "function") return Promise.resolve(false);
    return sdk.isBlacklisted().then(
      function (blocked) {
        return !!blocked;
      },
      function (err) {
        console.warn("[Y8Bridge] isBlacklisted failed:", err);
        return false;
      }
    );
  },

  showBlacklistOverlay: function () {
    if (typeof document === "undefined") return;
    var existing = document.getElementById("y8BlacklistOverlay");
    if (existing) return;
    var overlay = document.createElement("div");
    overlay.id = "y8BlacklistOverlay";
    overlay.setAttribute("role", "alertdialog");
    overlay.setAttribute("aria-modal", "true");
    overlay.style.cssText =
      "position:fixed;inset:0;z-index:99999;display:flex;align-items:center;justify-content:center;" +
      "background:rgba(8,16,32,0.92);color:#fff;font:600 18px/1.4 system-ui,sans-serif;text-align:center;padding:24px;";
    overlay.innerHTML =
      '<div style="max-width:420px">' +
      "<p>Please play Catch'n'Merge on the official Y8 site.</p>" +
      '<p style="margin-top:16px"><a href="' +
      Y8_OFFICIAL_GAME_URL +
      '" style="color:#7dd3fc">Open on Y8.com</a></p>' +
      "</div>";
    document.body.appendChild(overlay);
  },

  /**
   * Load Y8 cloud blobs into localStorage before Game.init (logged-in only).
   * @param {string} progressStorageKey opaque local key for progress
   * @param {string} bestScoreStorageKey opaque local key for best score
   * @param {string} economyStorageKey opaque local key for lifetime economy
   */
  syncCloudToLocal: function (progressStorageKey, bestScoreStorageKey, economyStorageKey) {
    if (!enabled || !hasLoggedInUser()) return Promise.resolve();
    var sdk = getSdk();
    if (!sdk || typeof sdk.loadData !== "function") return Promise.resolve();
    var storage = typeof localStorage !== "undefined" ? localStorage : null;
    if (!storage) return Promise.resolve();

    function loadKey(cloudKey) {
      return sdk.loadData({ key: cloudKey }).then(function (value) {
        if (value == null || value === "") return null;
        return typeof value === "string" ? value : String(value);
      });
    }

    return Promise.all([
      loadKey(Y8_CLOUD_KEY_PROGRESS),
      loadKey(Y8_CLOUD_KEY_BEST_SCORE),
      loadKey(Y8_CLOUD_KEY_ECONOMY),
    ]).then(function (values) {
      var pairs = [
        [progressStorageKey, values[0]],
        [bestScoreStorageKey, values[1]],
        [economyStorageKey, values[2]],
      ];
      for (var i = 0; i < pairs.length; i++) {
        var localKey = pairs[i][0];
        var blob = pairs[i][1];
        if (!blob) continue;
        try {
          storage.setItem(localKey, blob);
        } catch (e) {
          console.warn("[Y8Bridge] cloud → localStorage failed:", e);
        }
      }
    });
  },

  /**
   * Immediate checkpoint relay to Y8 cloud (logged-in only).
   */
  relayProgressSaved: function (localKey, encodedValue, progressStorageKey, bestScoreStorageKey, economyStorageKey) {
    if (!enabled || !hasLoggedInUser()) return Promise.resolve();
    var cloudKey = mapLocalKeyToCloudKey(localKey, progressStorageKey, bestScoreStorageKey, economyStorageKey);
    if (!cloudKey || encodedValue == null) return Promise.resolve();
    var sdk = getSdk();
    if (!sdk || typeof sdk.saveData !== "function") return Promise.resolve();
    return sdk
      .saveData({
        key: cloudKey,
        value: String(encodedValue),
        retries: true,
      })
      .catch(function (err) {
        console.warn("[Y8Bridge] saveData(" + cloudKey + ") failed:", err);
      });
  },

  submitLeaderboardScore: function (params) {
    if (!enabled || !hasLoggedInUser()) return Promise.resolve(false);
    var sdk = getSdk();
    if (!sdk || typeof sdk.saveLeaderboardScore !== "function") return Promise.resolve(false);
    var points = normalizeLeaderboardScore(params && params.points);
    if (points <= 0) return Promise.resolve(false);
    var playerName = sanitizeLeaderboardName(params && params.playerName);
    return sdk
      .saveLeaderboardScore({
        table: Y8_LEADERBOARD_TABLE,
        points: points,
        allowDuplicates: false,
        highest: true,
        playerName: playerName,
      })
      .then(function () {
        return true;
      })
      .catch(function (err) {
        console.warn("[Y8Bridge] saveLeaderboardScore failed:", err);
        return false;
      });
  },

  /**
   * Play Again interstitial whenever Y8 ads are available.
   * Not gated on `runIndex` — the first lifetime Play Again still qualifies.
   */
  shouldShowPlayAgainInterstitial: function (game) {
    return !!(enabled && game);
  },

  showBrowseInterstitial: function (name) {
    return Y8Bridge._showTypedInterstitial("browse", name || INTERSTITIAL_AD_NAME_PLAY_AGAIN);
  },

  showNextInterstitial: function (name) {
    return Y8Bridge._showTypedInterstitial("next", name || INTERSTITIAL_AD_NAME_NEXT_DAY);
  },

  /**
   * Show a typed interstitial. Same resume timing as {@link requestRewardedAd}:
   * settle only on `afterAd` / `adBreakDone` (ad UI closed). Never on the
   * `showAd` Promise — AFP often resolves that when the break starts.
   * Interstitials have no `adViewed`; that reward-only signal must not resume.
   * @param {"browse"|"next"|"start"|"pause"} type
   * @param {string} name
   * @returns {Promise<void>}
   */
  _showTypedInterstitial: function (type, name) {
    if (!enabled) return Promise.resolve();
    var sdk = getSdk();
    if (!sdk || typeof sdk.showAd !== "function") return Promise.resolve();
    var sm = adAudioSyncSoundManager;
    return new Promise(function (resolve) {
      var settled = false;

      function settle() {
        if (settled) return;
        settled = true;
        resolve();
      }

      /** Same gate as rewarded `completeBreak`: resume only after the ad closes. */
      function completeBreak() {
        if (settled) return;
        if (sm) restoreGameAudio(sm);
        settle();
      }

      try {
        sdk
          .showAd({
            type: type,
            name: name,
            beforeAd: function () {
              if (sm) muteGameAudio(sm);
            },
            afterAd: function () {
              completeBreak();
            },
            adBreakDone: function () {
              completeBreak();
            },
          })
          // Do not settle on promise resolve — AFP often resolves when the break
          // starts, before afterAd. Callbacks own resume timing.
          .catch(function (err) {
            console.warn("[Y8Bridge] " + type + " ad failed:", err);
            if (sm) restoreGameAudio(sm);
            settle();
          });
      } catch (e) {
        console.warn("[Y8Bridge] showAd(" + type + ") failed:", e);
        if (sm) restoreGameAudio(sm);
        settle();
      }
    });
  },

  /**
   * @param {{
   *   name?: string,
   *   onStarted?: () => void,
   *   onFinished?: () => void,
   *   onError?: (err: unknown) => void
   * }} [options]
   * @returns {Promise<boolean>}
   */
  requestRewardedAd: function (options) {
    var opts = options || {};
    var started = typeof opts.onStarted === "function" ? opts.onStarted : null;
    var finished = typeof opts.onFinished === "function" ? opts.onFinished : null;
    var onError = typeof opts.onError === "function" ? opts.onError : null;
    var placementName =
      typeof opts.name === "string" && opts.name.trim() ? opts.name.trim() : "reward";

    function grantLocally() {
      if (started) started();
      if (finished) finished();
      return true;
    }

    return Y8Bridge.ensureInitialized().then(function () {
      if (!enabled) return grantLocally();
      var sdk = getSdk();
      if (!sdk || typeof sdk.showAd !== "function") {
        if (onError) onError(new Error("Y8 reward ad unavailable"));
        return false;
      }
      return new Promise(function (resolve) {
        var settled = false;
        /** Set by adViewed; grant only after the ad UI closes (afterAd / adBreakDone). */
        var earned = false;
        var sm = adAudioSyncSoundManager;

        function settle(ok) {
          if (settled) return;
          settled = true;
          resolve(!!ok);
        }

        function completeBreak() {
          if (settled) return;
          if (sm) restoreGameAudio(sm);
          if (earned) {
            if (finished) finished();
            settle(true);
            return;
          }
          settle(false);
        }

        try {
          sdk
            .showAd({
              type: "reward",
              name: placementName,
              beforeAd: function () {
                if (sm) muteGameAudio(sm);
                if (started) started();
              },
              beforeReward: function (showAdFn) {
                showAdFn();
              },
              // AFP may fire adViewed when the close button appears — do not grant yet.
              adViewed: function () {
                earned = true;
              },
              adDismissed: function () {
                earned = false;
              },
              afterAd: function () {
                completeBreak();
              },
              adBreakDone: function () {
                completeBreak();
              },
            })
            // Do not settle on promise resolve — AFP often resolves when the break
            // starts, before adViewed/afterAd. Callbacks own grant timing.
            .catch(function (err) {
              console.warn("[Y8Bridge] reward ad failed:", err);
              if (onError) onError(err);
              if (sm) restoreGameAudio(sm);
              settle(false);
            });
        } catch (e) {
          console.warn("[Y8Bridge] showAd(reward) failed:", e);
          if (onError) onError(e);
          if (sm) restoreGameAudio(sm);
          settle(false);
        }
      });
    });
  },

  /**
   * @param {{ setMuted?: (muted: boolean) => void, setBgmPaused?: (paused: boolean) => void }} soundManager
   */
  installAdAudioSync: function (soundManager) {
    if (adAudioSyncInstalled) return;
    if (!soundManager || typeof soundManager.setMuted !== "function") return;
    adAudioSyncSoundManager = soundManager;
    adAudioSyncInstalled = true;
  },
};